/**
 * QuickEditDialog Component
 *
 * Confirmation dialog for single-cell quick edit.
 * Shows the change that will be made and allows user to confirm or cancel.
 */

import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Code2, Save } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../../components/ui/alert-dialog';
import { Button } from '../../../../components/ui/button';
import { QueryPreviewDialog } from './QueryPreviewDialog';
import type { PendingUpdate } from '../../core/types/workspaceTypes';
import type { TableExplorerApplyChangesResult } from '../../../../types/tableExplorer';
import { previewTableExplorerChanges } from '../services/tableExplorerApi';
import type { DatabaseEngineId } from '@/core/engines';
import { safeToLocaleString } from '../../../../utils/dateUtils';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type QuickEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supportsQueryPreview: boolean;
  connectionId: string;
  engineId?: DatabaseEngineId;
  database: string;
  schema: string;
  tableName: string;
  primaryKey: Record<string, unknown>;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  onApply: (change: PendingUpdate) => Promise<TableExplorerApplyChangesResult>;
  onOpenInQueryEditor?: (query: string) => void;
};

/**
 * Format a value for display
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'object') {
    if (value instanceof Date) return safeToLocaleString(value);
    return JSON.stringify(value);
  }
  return String(value);
}

export function QuickEditDialog({
  open,
  onOpenChange,
  supportsQueryPreview,
  connectionId,
  engineId,
  database,
  schema,
  tableName,
  primaryKey,
  field,
  oldValue,
  newValue,
  onApply,
  onOpenInQueryEditor,
}: QuickEditDialogProps) {
  const fullTableName = schema ? `${schema}.${tableName}` : tableName;
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewSql, setPreviewSql] = useState('');

  useEffect(() => {
    if (open) return;
    setError(null);
    setIsExecuting(false);
    setIsPreviewOpen(false);
    setPreviewSql('');
  }, [open]);

  const handleExecute = useCallback(async (): Promise<TableExplorerApplyChangesResult> => {
    setIsExecuting(true);
    setError(null);

    try {
      const result = await onApply({
        type: 'update',
        primaryKey,
        field,
        oldValue,
        newValue,
      });

      if (result.success) {
        // When the preview dialog (QueryPreviewDialog) is the caller, it
        // already closes itself on success right after this resolves. If we
        // close the outer AlertDialog in the same frame, both modal dialogs
        // unmount in the same React commit and Radix' two `RemoveScroll`
        // cleanups race — both set `style="pointer-events: none"` on <body>
        // but only one cleanup runs, leaving the body stuck (intermittently)
        // and freezing the app.
        //
        // Defer the outer close so Radix' RemoveScroll for the inner dialog
        // fully unmounts (and clears its body lock) before the outer one
        // starts its own teardown. Mirrors the 150-250ms deferral pattern
        // already used in `ExitEditModeDialog` and `RowDetailsDataTab`'s
        // expanded-edit→quick-edit handoff.
        setTimeout(() => onOpenChange(false), 150);
        return result;
      }
      const errMsg = result.error || 'Failed to save change. Please try again.';
      setError(errMsg);
      return { success: false, error: errMsg };
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errMsg);
      return { success: false, error: errMsg };
    } finally {
      setIsExecuting(false);
    }
  }, [onApply, primaryKey, field, oldValue, newValue, onOpenChange]);

  const handleCancel = () => {
    setError(null);
    onOpenChange(false);
  };

  const handlePreviewEditQuery = useCallback(async () => {
    if (!supportsQueryPreview) return;

    // Note: schema can be empty for SQLite (which doesn't use schemas)
    if (!connectionId || !database || !tableName) {
      setPreviewSql('-- Missing context for SQL preview');
      setIsPreviewOpen(true);
      return;
    }

    setPreviewSql('-- Loading...');
    setIsPreviewOpen(true);

    try {
      const result = await previewTableExplorerChanges({
        connectionId,
        table: { database, schema, tableName },
        changes: [
          {
            type: 'update',
            primaryKey,
            field,
            oldValue,
            newValue,
          },
        ],
      });

      if (result.success) {
        setPreviewSql(result.sql || '');
        return;
      }

      setPreviewSql(`-- Failed to generate preview: ${result.error || 'Unknown error'}`);
    } catch (err) {
      setPreviewSql(`-- Failed to generate preview: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [supportsQueryPreview, connectionId, database, schema, tableName, primaryKey, field, oldValue, newValue]);

  // Check if values are actually different
  const valuesAreSame = oldValue === newValue ||
    (oldValue === null && newValue === null) ||
    (formatValue(oldValue) === formatValue(newValue));

  return (
    <>
      <AlertDialog open={open} onOpenChange={onOpenChange}>
        <AlertDialogContent className="max-w-2xl max-h-[85vh] flex flex-col" data-testid="quick-edit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Save Change?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 overflow-hidden flex-1 min-h-0">
                <p>You are about to update the following value:</p>

                <div className="bg-muted rounded-md p-3 space-y-2 text-sm overflow-auto max-h-[50vh]">
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-muted-foreground">Table:</span>
                    <span className="font-medium break-all">{fullTableName}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-muted-foreground">Column:</span>
                    <span className="font-medium break-all">{field}</span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-muted-foreground">From:</span>
                    <span className="font-mono text-red-600 dark:text-red-400 line-through break-all whitespace-pre-wrap">
                      {formatValue(oldValue)}
                    </span>
                  </div>
                  <div className="grid grid-cols-[80px_1fr] gap-2">
                    <span className="text-muted-foreground">To:</span>
                    <span className="font-mono text-green-600 dark:text-green-400 break-all whitespace-pre-wrap">
                      {formatValue(newValue)}
                    </span>
                  </div>
                </div>

                {valuesAreSame && (
                  <div className="flex items-center gap-2 text-warning text-sm">
                    <AlertTriangle className="h-4 w-4" />
                    <span>The value hasn't changed.</span>
                  </div>
                )}

                {error && (
                  <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                    {error}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isExecuting}
            >
              Cancel
            </Button>

            {supportsQueryPreview && (
              <Button
                variant="outline"
                onClick={handlePreviewEditQuery}
                disabled={isExecuting || valuesAreSame}
                data-testid="preview-edit-query-button"
              >
                <Code2 className="h-4 w-4 mr-2" />
                Preview edit query
              </Button>
            )}

            <Button
              onClick={handleExecute}
              disabled={isExecuting || valuesAreSame}
              data-testid="quick-edit-save-button"
            >
              {isExecuting ? (
                <>
                  <RandomDotmSpinner size={16} className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {supportsQueryPreview && (
        <QueryPreviewDialog
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          query={previewSql}
          engineId={engineId}
          onOpenInQueryEditor={onOpenInQueryEditor}
          onExecute={handleExecute}
          executeLabel="Save"
          executingLabel="Saving..."
          executeDisabled={valuesAreSame}
          title="Edit Query Preview"
          description={`Generated SQL script for ${fullTableName}`}
          testIdPrefix="edit-query-preview"
        />
      )}
    </>
  );
}
